import Minze, { MinzeElement, Reactive, EventListeners, Attrs } from 'minze'
import MainModule from '../../../modules/main'
import { Callback } from '../../../modules/callback'
import { config, MODALS } from '../../../constants'
import { Routing } from '../../../modules/routing'
import { EMAIL_REGEX, PASSWORD_REGEX } from '../../../modules/utils'
import { Locale } from '../../../modules/locale'
import { Font } from '../../../constants'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import axios from 'redaxios'

Locale.getInstance().registerKeySet('fr', 'create-event-search-or-add', {
  title: 'Recherchez ou créez votre événement',
  festivalTitle: 'Recherchez ou créez votre festival',
  description:
    'Commencez par rechercher votre événement. La Vitrine est connectée à plusieurs sources de données et il se peut que votre événement soit déjà répertorié. S’il n’apparaît pas, alors créez le.',
  festivalDescription:
    "Commencez par rechercher votre festival. La Vitrine est connectée à plusieurs sources de données et il se peut que votre festival soit déjà répertorié. S'il n'apparaît pas, alors créez-le.",
  eventSearchTitle: 'Ajouter des représentations à un événement existant',
  festivalSearchTitle: 'Ajouter des représentations à un festival existant',
  createEventButtonTitle: 'Créer un nouvel événement non répertorié sur le site de La Vitrine',
  createFestivalButtonTitle: 'Créer un nouveau festival non répertorié sur le site de La Vitrine',
  createEventButtonLabel: 'Créer un événement',
  createFestivalButtonLabel: 'Créer un festival',
  createEventButtonTooltip: '',
  or: 'ou',
})

Locale.getInstance().registerKeySet('en', 'create-event-search-or-add', {
  title: 'Search for or create your event',
  festivalTitle: 'Search for or create your festival',
  description:
    'Start by searching for your event. La Vitrine is connected to several data sources and your event may already be a part of our database. If it does not pop up, proceed to create it.',
  festivalDescription:
    'Start by searching for your festival. La Vitrine is connected to several data sources and your festival may already be a part of our database. If it does not pop up, proceed to create it.',
  eventSearchTitle: 'Add showings to an existing event',
  festivalSearchTitle: 'Add shows to an existing festival',
  createEventButtonTitle: 'Create a new event currently not on the La Vitrine website',
  createFestivalButtonTitle: 'Create a new festival currently not listed on the La Vitrine website',
  createEventButtonLabel: 'Create an event',
  createFestivalButtonLabel: 'Create a festival',
  createEventButtonTooltip: '',
  or: 'or',
})

export interface LvcCreateEventSearchOrAdd {
  flowGroup: string
  flowStep: string
  tooltipOpened: boolean
}

export interface searchOrAddEventData {
  eventName?: string
  eventData?: any
}

export class LvcCreateEventSearchOrAdd
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private core: IFlowCore | undefined
  attrs: Attrs = [
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['existingEventData', '', false],
    ['newEventName', '', false],
    ['eventSearchOrAddData', {}, false],
    ['tooltipOpened', false],
  ]

  openTooltip = () => {
    this.tooltipOpened = true
  }

  closeTooltip = () => {
    this.tooltipOpened = false
  }

  onChangeEventSearchField = async (event: any) => {
    this.existingEventData = event.detail[0]

    let existingRepresentations = []
    try {
      const id = (this.existingEventData as any)?.id
      const schema = 'events'
      const result: any = await axios.get(
        `${config.backendApi}/content/getOffersForEventV2?id=${id}&schema=${schema}`
      )
      existingRepresentations = result.data || []
    } catch (error) {}

    this.eventSearchOrAddData = {
      eventData: this.existingEventData,
      eventName: '',
      existingRepresentations: existingRepresentations,
    }

    if (this.existingEventData) {
      if (this.core) {
        this.core.getData().control_modifyingExistingEvent = true
        this.core.goTo('eventConfirmation')
      }
    }
  }

  onChangeEventAdd = (event: any) => {
    this.newEventName = event.detail === 1 ? '' : event.detail || ''
    this.eventSearchOrAddData = {
      eventData: {},
      eventName: this.newEventName,
    }
    if (this.core) {
      this.core.getData().control_modifyingExistingEvent = undefined
      this.core.next()
    }
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)

    this.core = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  errors: any = {
    image: false,
    imageCredit: false,
    altTextFr: false,
    altTextEn: false,
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // We get the data from the flowCore and we set the values of the inputs
    const entireEventData = data
    this.eventSearchOrAddData = {
      eventData: entireEventData?.createEventSearchOrAdd?.eventData,
      eventName: entireEventData?.createEventSearchOrAdd?.eventName,
    }

    this.existingEventData = entireEventData?.createEventSearchOrAdd?.eventData
    this.newEventName = entireEventData?.createEventSearchOrAdd?.eventName
  }

  public async serialize(): Promise<unknown> {
    return this.eventSearchOrAddData
  }

  public async validate(): Promise<boolean> {
    return true
  }

  html = () =>
    `<div class="container">


    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey(
          `create-event-search-or-add.${this.dataType === 'festival' ? 'festivalTitle' : 'title'}`
        )}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey(
          `create-event-search-or-add.${
            this.dataType === 'festival' ? 'festivalDescription' : 'description'
          }`
        )}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px">
      <lvc-event-search-field
        title="${Locale.getKey(
          `create-event-search-or-add.${
            this.dataType === 'festival' ? 'festivalSearchTitle' : 'eventSearchTitle'
          }`
        )}"
        events-type="${this.dataType === 'festival' ? 'festivals' : 'events'}"
        disable-creation="true"
      ></lvc-event-search-field>
      <lvc-box
        display="flex"
        justifyContent="center"
        mt="32px"
        mb="24px"
      >
        <lvc-text>${Locale.getKey('create-event-search-or-add.or')}</lvc-text>
      </lvc-box>
      <div class="title">
        ${
          this.title ||
          Locale.getKey(
            `create-event-search-or-add.${
              this.dataType === 'festival' ? 'createFestivalButtonTitle' : 'createEventButtonTitle'
            }`
          )
        }
<!--        <lvc-help-icon name="helpIcon"></lvc-help-icon>-->
      </div>
      ${this.tooltipOpened ? `<div class="close-overlay"></div>` : ''}
      <dialog id="events-tooltip" class="tooltip ${this.tooltipOpened ? 'open' : ''}">
        <div class="events-tooltip-content" slot="tooltip-content">
          <lvc-text font-preset="small">
            ${Locale.getKey(
              `create-event-search-or-add.${
                this.dataType === 'festival'
                  ? 'createFestivalButtonTooltip'
                  : 'createEventButtonTooltip'
              }`
            )}
          </lvc-text>
        </div>
      </dialog>
      <lvc-button
        id="create-new-event"
        display="flex"
        align-items="center"
        justify-content="center"
      >
        <lvc-box
          columnGap="8px"
          display="flex"
          align-items="center"
          justify-content="center"
          height="auto"
        >
          <lvc-box width="20px" height="20px">
            <lvc-add-icon></lvc-add-icon>
          </lvc-box>
          ${Locale.getKey(
            `create-event-search-or-add.${
              this.dataType === 'festival' ? 'createFestivalButtonLabel' : 'createEventButtonLabel'
            }`
          )}
        </lvc-box>
      </lvc-button>
    </lvc-box>

  </div>
    `

  css() {
    return `
      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        display: flex;


        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 4px;
      }
    
      lvc-help-icon {
        display:flex;
        align-items: center;
        margin-left: 4px;
        cursor: pointer;
      }

      .tooltip-dialog {
        display: none;
        position: absolute;
        background-color: #fff;
        border: 1px solid #ccc;
        padding: 10px;
        z-index: 3; /* Ensure the dialog appears above other elements */
        /* Add any other styles for the dialog */
      }

      dialog.open {
        margin-top: 5px;
        display: block;
        z-index: 1;
        border: 1px solid rgba(30, 0, 125, 0.3);
        box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
        border-radius: 8px;
        padding: 16px;
        font-weight: ${Font.weight.medium};
        font-size: 14px;
        line-height: 150%;
        color: #1E007D;
        max-width: 90vw;
        width: 340px;
        margin-left: auto;
      }

      .close-overlay {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
      }
    `
  }

  eventListeners: EventListeners = [
    ['lvc-event-search-field', 'change:clickedEvent', this.onChangeEventSearchField.bind(this)],
    ['lvc-event-search-field', 'change:newEvent', this.onChangeEventAdd.bind(this)],
    ['[name="helpIcon"]', 'click', this.openTooltip],
    ['.close-overlay', 'click', this.closeTooltip],
    ['#create-new-event', 'click', this.onChangeEventAdd],
  ]
}
